"use client"

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { formatNumeric } from "@/lib/utils/stringUtils"
import { cn } from "@/lib/utils"

type TrafficSourceItem = {
  primary: string
  secondary?: string | null
  secondaryFull?: string | null
  value: number
  share: number
}

type TrafficSourcesListProps = {
  items: TrafficSourceItem[]
  className?: string
  title?: string
  /** Bar fill token — chart-1 default */
  barClassName?: string
}

/**
 * Compact traffic-source breakdown for Pages / Forms expand rows.
 * Primary = source/medium; secondary = campaign (soft-truncated) with full tooltip.
 */
export function TrafficSourcesList({
  items,
  className,
  title = "Traffic Sources",
  barClassName = "bg-chart-1",
}: TrafficSourcesListProps) {
  if (items.length === 0) return null

  return (
    <div className={cn("min-w-0", className)}>
      <h4 className="text-xs font-medium text-muted-foreground mb-2.5">
        {title}
      </h4>
      <div className="space-y-2.5">
        {items.map((item, idx) => {
          const tip = item.secondaryFull || item.secondary || item.primary
          return (
            <div key={`${item.primary}-${idx}`} className="space-y-1 min-w-0">
              <div className="flex items-start justify-between gap-3 min-w-0">
                <div className="min-w-0 flex-1">
                  <p
                    className="text-xs font-medium text-foreground truncate"
                    title={item.primary}
                  >
                    {item.primary}
                  </p>
                  {item.secondary && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <p className="text-[11px] text-muted-foreground/70 truncate cursor-default max-w-full">
                          {item.secondary}
                        </p>
                      </TooltipTrigger>
                      <TooltipContent
                        side="bottom"
                        align="start"
                        className="max-w-[320px] text-[11px] break-all"
                      >
                        {tip}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
                <div className="flex items-baseline gap-1.5 tabular-nums shrink-0">
                  <span className="text-xs font-medium text-foreground">
                    {formatNumeric(item.value)}
                  </span>
                  <span className="text-[11px] text-muted-foreground/70">
                    {(item.share * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
              <div className="h-1 bg-muted/30 rounded-full overflow-hidden">
                <div
                  className={cn("h-full rounded-full transition-all", barClassName)}
                  style={{ width: `${Math.min(item.share * 100, 100)}%` }}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default TrafficSourcesList
